import {
  SupplierRelationshipListNewResponseType,
  SupplierRelationshipPaymentDueCalculateFromType,
  SupplierRelationshipPaymentTermsType,
} from "@sellar-io/api-v2/types";
import { capitalize, formatCurrency } from "@sellar-io/common";
import { UseQueryResult } from "@tanstack/react-query";
import { PaginationState, OnChangeFn } from "@tanstack/react-table";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { twMerge } from "tailwind-merge";
import { useShallow } from "zustand/react/shallow";
import { Icon, ProfilePicture } from "../../../components";
import { PaymentTermsIndicator } from "../../../components/paymentTypeIndicator/PaymentTermsIndicator";
import { usePaymentTermsIndicator } from "../../../components/paymentTypeIndicator/usePaymentTermsIndicator";
import { useFeatureFlagStore } from "../../../hooks/useFeatureFlags";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { useCustomerListStore } from "./CustomerListStore";
import { CustomerListCreditTermsRequestDialog } from "./Dialogs/CustomerListCreditTermsRequestDialog";
import { CustomerListSortType } from "./CustomerListFilterTypes";
import {
  TablePaginated,
  IconType,
  createSellarColumnHelper,
} from "@sellar-io/components";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";
import { BadgeZinc } from "~/components/badge/Badge";
import { Tooltip } from "@sellar-io/components";
import { TooltipTrigger } from "@sellar-io/components";
import { TooltipContent } from "@sellar-io/components";

export const CustomerListTable = ({
  connectionListQuery,
  onClickCustomer,
  handleTableSort,
  pagination,
  setPagination,
  customerCount,
}: {
  connectionListQuery: UseQueryResult<SupplierRelationshipListNewResponseType>;
  onClickCustomer: (id: number) => void;
  handleTableSort: (value: CustomerListSortType | null) => void;
  pagination: PaginationState;
  setPagination: OnChangeFn<PaginationState>;
  customerCount: number;
}) => {
  const unbundlingRelease2bEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingRelease2B",
  );

  const unbundlingEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingReleaseFinal",
  );

  const { data } = connectionListQuery;

  const { scrollOffset, columnSort } = useCustomerListStore(
    useShallow((state) => ({
      scrollOffset: state.scrollOffset,
      columnSort: state.columnSort,
    })),
  );

  const pageCount = Math.ceil(customerCount / pagination.pageSize);

  const columnHelper =
    createSellarColumnHelper<SupplierRelationshipListNewResponseType[number]>();

  const columns = [
    columnHelper.accessor((row) => row, {
      id: "name",
      header: "Customer",
      cell: (info) => {
        const connection = info.getValue();
        const Business = connection.Retailer.Business;
        return (
          <div
            className={"group flex cursor-pointer items-center"}
            onClick={() => onClickCustomer(connection.id)}
          >
            <figure className="relative">
              <ProfilePicture
                size="small"
                name={Business.name}
                logoUrl={Business.logoUrl}
                className="mr-2"
              />
              {!connection.approved && (
                <span className="absolute right-[6px] bottom-0 h-2 w-2 rounded-full border border-white bg-indigo-600" />
              )}
            </figure>
            <div className="flex flex-col gap-1">
              <span
                className={twMerge(
                  "flex items-center",
                  connection.approved
                    ? "font-medium group-hover:font-semibold"
                    : "font-bold group-hover:font-extrabold",
                )}
              >
                <span className="group-hover:underline">{Business.name}</span>
                <Icon
                  size={0.8}
                  className="hidden group-hover:inline"
                  icon={"CHEVRON_RIGHT"}
                />
              </span>
              {unbundlingEnabled && (
                <span className="text-xs text-zinc-500">
                  {Business.DeliveryAddress.postalCode}
                </span>
              )}
            </div>
          </div>
        );
      },
    }),
    ...(!unbundlingEnabled
      ? [
          columnHelper.accessor("Retailer", {
            id: "postcode",
            header: "Postcode",
            enableSorting: false,
            cell: (info) => {
              const { Business } = info.getValue();
              return Business.DeliveryAddress.postalCode;
            },
          }),
        ]
      : []),
    ...(unbundlingEnabled
      ? [
          columnHelper.accessor("Relationship", {
            id: "customerZone",
            header: "Delivery zone",
            enableSorting: false,
            cell: (info) =>
              info.getValue()?.CustomerZone?.name ?? "Default zone",
          }),
          columnHelper.accessor((row) => row, {
            id: "deliveryMethods",
            header: "Delivery methods",
            enableSorting: false,
            cell: (info) => {
              const connection = info.getValue();
              return <DeliveryMethodsCell connection={connection} />;
            },
          }),
        ]
      : [
          columnHelper.accessor("fulfillmentType", {
            header: "Delivery tier",
            enableSorting: false,
            cell: (info) => {
              const tier =
                info.getValue() === "haulier" ? "Pallet" : info.getValue();
              if (tier === "sellarDistribution") return "Sellar Distribition";
              return tier.charAt(0).toUpperCase() + tier.slice(1);
            },
          }),
        ]),

    columnHelper.accessor("PriceTier", {
      id: "priceTier",
      header: unbundlingRelease2bEnabled ? "Price list" : "Price tier",
      enableSorting: false,
      cell: (info) => info.getValue()?.name ?? "-",
    }),

    columnHelper.accessor("AvailabilityTier", {
      id: "availabilityTier",
      header: "Product catalogue",
      enableSorting: false,
      cell: (info) => info.getValue()?.name ?? "-",
    }),

    columnHelper.accessor((row) => row, {
      header: "Terms",
      enableSorting: false,
      cell: (info) => {
        const connection = info.getValue();
        return <PaymentTermsColumnContent connection={connection} />;
      },
    }),
    columnHelper.accessor("SupplierRetailerConnectionAnalytics", {
      id: "last_ordered",
      header: "Last ordered",
      sortDescFirst: true,
      cell: (info) => {
        const Analytics = info.getValue();
        if (!Analytics?.lastOrderDate) return "Yet to order";
        return capitalize(dayjs(Analytics.lastOrderDate).fromNow());
      },
    }),
    columnHelper.accessor("SupplierRetailerConnectionAnalytics", {
      id: "average_order_value",
      header: "Avg order size",
      cell: (info) => {
        const Analytics = info.getValue();
        const averageOrderValue = Analytics?.averageOrderValue ?? 0;
        return averageOrderValue === 0
          ? "-"
          : formatCurrency(averageOrderValue);
      },
    }),
  ];

  const colWidthsGridClasses = unbundlingEnabled
    ? "grid-cols-[minmax(250px,_1fr)_minmax(100px,_1fr)_minmax(175px,_1fr)_minmax(120px,_1fr)_minmax(120px,_1fr)_minmax(140px,_1fr)_minmax(105px,_1fr)_minmax(110px,_1fr)]"
    : "grid-cols-[minmax(280px,_1fr)_minmax(90px,_1fr)_minmax(90px,_1fr)_minmax(140px,_1fr)_minmax(140px,_1fr)_minmax(230px,_1fr)_minmax(115px,_1fr)_minmax(125px,_1fr)]";

  // Memoize data for performance
  const flatData = useMemo(() => data ?? [], [data]);

  return (
    <TablePaginated
      columns={columns}
      data={flatData}
      initialOffset={scrollOffset}
      setScrollOffset={(offset) => {
        useCustomerListStore.setState({ scrollOffset: offset });
      }}
      pageCount={pageCount}
      pagination={pagination}
      setPagination={setPagination}
      columnSortName={columnSort}
      handleTableSort={handleTableSort}
      noResultsText="Your search returned 0 results"
      paginationLabel="customers per page"
      gridClasses={colWidthsGridClasses}
      onRowClick={(row) => onClickCustomer(row.id)}
    />
  );
};

const iconMap: Record<string, IconType> = {
  direct: "TRUCK_OUTLINE",
  courier: "PACKAGE_VARIANT_CLOSED",
  haulier: "PALLET",
};

const DeliveryMethodsCell = ({
  connection,
}: {
  connection: SupplierRelationshipListNewResponseType[number];
}) => {
  const { fulfillmentMethods, fulfillmentType } = connection;

  const isSellarDistribution = fulfillmentType === "sellarDistribution";
  const isCollection = fulfillmentType === "collection";
  const isDirect = fulfillmentType === "direct";

  // Get methods excluding the default method
  const otherMethods = fulfillmentMethods.filter((m) => !m.isDefault);

  // Find the default method from fulfillmentMethods
  const defaultMethod = fulfillmentMethods.find((m) => m.isDefault);

  // Build sorted list for tooltip: SD/collection/direct first if applicable, then other methods
  const sortedMethods = [
    ...(isSellarDistribution
      ? [
          {
            id: -1,
            name: "Sellar Distribution",
            type: "sellarDistribution",
            isDefault: true,
          },
        ]
      : isCollection
        ? [{ id: -1, name: "Collection", type: "collection", isDefault: true }]
        : isDirect
          ? [{ id: -1, name: "Direct", type: "direct", isDefault: true }]
          : []),
    ...otherMethods,
  ];

  // Get default label and icon with fallback for direct delivery
  const defaultLabel = isSellarDistribution
    ? "Sellar Distribution"
    : isCollection
      ? "Collection"
      : isDirect
        ? "Direct"
        : capitalize(defaultMethod?.type || "");

  const defaultIcon = isSellarDistribution
    ? "SELLAR_DISTRIBUTION_TRUCK"
    : isCollection
      ? "FACTORY_OUTLINE"
      : isDirect
        ? "TRUCK_OUTLINE"
        : (iconMap[defaultMethod?.type ?? ""] ?? null);

  if (!defaultLabel && otherMethods.length === 0) {
    return <span className="text-zinc-400">-</span>;
  }

  // For direct delivery, if there are no other methods, don't show the +N badge
  const showAdditionalMethodsBadge = otherMethods.length > 0;

  return (
    <div className="flex items-center gap-2">
      {defaultLabel && defaultIcon && (
        <BadgeZinc icon={defaultIcon as any} className="font-normal">
          {defaultLabel}
        </BadgeZinc>
      )}
      {showAdditionalMethodsBadge && (
        <Tooltip placement="bottom">
          <TooltipTrigger className="w-fit">
            <BadgeZinc className="font-normal">
              +{otherMethods.length}
            </BadgeZinc>
          </TooltipTrigger>
          <TooltipContent>
            <div
              className={
                "max-w-[300px] rounded-xl border border-zinc-300 bg-white px-4 py-3"
              }
            >
              {/* Tooltip tail */}
              <div className="absolute top-[-7px] left-1/2 -translate-x-1/2">
                <div className="h-3 w-3 rotate-45 transform border-t border-l border-gray-300 bg-white"></div>
              </div>

              <div>
                <div className="text-sm font-semibold text-zinc-800">
                  Available delivery methods
                </div>
                <ul className="list-disc pl-4 text-xs text-zinc-700">
                  {sortedMethods.map((method) => (
                    <li key={method.id}>
                      {method.name ?? capitalize(method.type)}
                      {method.isDefault && (
                        <span className="text-zinc-500"> (default)</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};

const PaymentTermsColumnContent = ({
  connection,
}: {
  connection: SupplierRelationshipListNewResponseType[number];
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [active, setActive] = useState<boolean>(false);
  const { isActive } = useFeatureFlagStore();
  const { business } = useSellarCurrentBusiness();
  const supplier = connection.Supplier;
  const { full, type } = usePaymentTermsIndicator({
    connection: {
      ...connection,
      paymentDueCalculateFrom:
        connection.paymentDueCalculateFrom as SupplierRelationshipPaymentDueCalculateFromType,
      paymentTerms:
        connection.paymentTerms as SupplierRelationshipPaymentTermsType,
    },
    invoiceCreationStage:
      business?.Supplier?.invoiceCreationStage ?? "confirmed",
    approved: connection.approved,
    paymentTermsRequest:
      connection.SupplierRetailerConnectionPaymentTermsRequest,
    hasDefaultPriceTier: !!business?.Supplier?.DefaultPriceTierId,
    supplier,
  });

  const isRequestedTerms = type === "termsRequested";
  const creditTermsRequestIdParam = searchParams.get("creditTermsRequestId")
    ? parseInt(searchParams.get("creditTermsRequestId") ?? "")
    : null;
  const hasPendingCreditTermsRequest =
    connection.SupplierRetailerConnectionPaymentTermsRequest?.status ===
    "pending";
  const existingCreditTermsId = hasPendingCreditTermsRequest
    ? connection.SupplierRetailerConnectionPaymentTermsRequest?.id
    : null;
  const isMatchingCreditTermsId =
    !!existingCreditTermsId &&
    !!creditTermsRequestIdParam &&
    existingCreditTermsId === creditTermsRequestIdParam;
  const autoOpenRequestId =
    type === "termsRequested" && isMatchingCreditTermsId
      ? creditTermsRequestIdParam
      : null;
  const [previousAutoOpenRequestId, setPreviousAutoOpenRequestId] =
    useState(autoOpenRequestId);
  if (autoOpenRequestId !== previousAutoOpenRequestId) {
    setPreviousAutoOpenRequestId(autoOpenRequestId);
    if (autoOpenRequestId) setActive(true);
  }
  return (
    <>
      {isActive("CHECKOUT_CREDIT_TERMS_REQUEST") ? (
        <div
          onClick={() => (isRequestedTerms ? setActive(true) : undefined)}
          className={twMerge(isRequestedTerms && "cursor-pointer")}
        >
          {full}
        </div>
      ) : (
        <PaymentTermsIndicator
          connection={{
            ...connection,
            paymentDueCalculateFrom:
              connection.paymentDueCalculateFrom as SupplierRelationshipPaymentDueCalculateFromType,
            paymentTerms:
              connection.paymentTerms as SupplierRelationshipPaymentTermsType,
          }}
          invoiceCreationStage={
            business?.Supplier?.invoiceCreationStage ?? "confirmed"
          }
          approved={connection.approved}
        />
      )}
      {connection.SupplierRetailerConnectionPaymentTermsRequest?.status ===
        "pending" && (
        <CustomerListCreditTermsRequestDialog
          active={active}
          onDismiss={() => {
            if (creditTermsRequestIdParam) {
              searchParams.delete("creditTermsRequestId");
              setSearchParams(searchParams);
            }
            setActive(false);
          }}
          connection={connection}
        />
      )}
    </>
  );
};
